import React, { useRef, useState, useEffect, useCallback } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  Box,
  Typography,
  Alert,
  AlertTitle,
  IconButton,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CameraAltIcon from "@mui/icons-material/CameraAlt";

interface IMEIScannerProps {
  open: boolean;
  onClose: () => void;
  onDetected: (imei: string) => void;
  title?: string;
}

export const IMEIScanner: React.FC<IMEIScannerProps> = ({
  open,
  onClose,
  onDetected,
  title = "Scanner de IMEI",
}) => {
  // Estados essenciais (idênticos ao TestBarCode)
  const [isScanning, setIsScanning] = useState(false);
  const [scannerStatus, setScannerStatus] = useState<
    "searching" | "found" | "idle"
  >("idle");
  const [activeZone, setActiveZone] = useState<
    "small" | "medium" | "large" | null
  >(null);
  const [isMobile, setIsMobile] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);

  // Refs para o scanner (idênticos ao TestBarCode)
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const codeReader = useRef<BrowserMultiFormatReader | null>(null);
  const scanIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const theme = useTheme();

  // Detectar se é dispositivo mobile (idêntico ao TestBarCode)
  useEffect(() => {
    const checkMobile = () => {
      const userAgent =
        navigator.userAgent || navigator.vendor || (window as any).opera;
      const isMobileDevice =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
          userAgent.toLowerCase()
        );
      const isTouchDevice =
        "ontouchstart" in window || navigator.maxTouchPoints > 0;

      //   console.log("Detectando mobile:", {
      //     isMobileDevice,
      //     isTouchDevice,
      //     userAgent,
      //   });
      setIsMobile(isMobileDevice || isTouchDevice);
    };

    checkMobile();
  }, []);

  // Configurar o leitor com configurações otimizadas (idêntico ao TestBarCode)
  const setupCodeReader = () => {
    codeReader.current = new BrowserMultiFormatReader();
  };

  // Função para escanear múltiplas áreas do viewFinder
  const scanMultipleAreas = useCallback(() => {
    if (!videoRef.current || !canvasRef.current || !codeReader.current) {
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    if (!ctx) return;

    // Dimensões do vídeo
    const { videoWidth, videoHeight } = video;

    if (videoWidth === 0 || videoHeight === 0) return;

    // Configurações dos 3 tamanhos de viewFinder
    const viewFinderSizes = {
      small: {
        width: isMobile ? 180 : 200,
        height: isMobile ? 50 : 55,
        label: "Muito Próximo",
      },
      medium: {
        width: isMobile ? 220 : 240,
        height: isMobile ? 65 : 70,
        label: "Distância Ideal",
      },
      large: {
        width: isMobile ? 280 : 300,
        height: isMobile ? 80 : 85,
        label: "Distante",
      },
    };

    // Calcular proporção entre vídeo e display
    const videoElement = video.getBoundingClientRect();
    const scaleX = videoWidth / videoElement.width;
    const scaleY = videoHeight / videoElement.height;

    // Testar cada zona em ordem de prioridade (small -> medium -> large)
    const zonesToTest = ["small", "medium", "large"] as const;

    // Função para testar uma zona específica
    const testZone = (zone: (typeof zonesToTest)[number]) => {
      const size = viewFinderSizes[zone];

      // Converter dimensões CSS para pixels do vídeo
      const viewFinderWidth = size.width * scaleX;
      const viewFinderHeight = size.height * scaleY;

      // Calcular posição central do viewFinder no vídeo
      const centerX = videoWidth / 2;
      const centerY = videoHeight / 2;
      const cropX = Math.max(0, centerX - viewFinderWidth / 2);
      const cropY = Math.max(0, centerY - viewFinderHeight / 2);
      const cropWidth = Math.min(viewFinderWidth, videoWidth - cropX);
      const cropHeight = Math.min(viewFinderHeight, videoHeight - cropY);

      // Configurar canvas para a área cropada
      canvas.width = cropWidth;
      canvas.height = cropHeight;

      // Desenhar apenas a área do viewFinder no canvas
      ctx.drawImage(
        video,
        cropX,
        cropY,
        cropWidth,
        cropHeight,
        0,
        0,
        cropWidth,
        cropHeight
      );

      // Tentar decodificar esta área
      try {
        const dataURL = canvas.toDataURL("image/png");

        codeReader.current
          ?.decodeFromImageUrl(dataURL)
          .then((result: any) => {
            if (result) {
              const detectedText = result.getText();

              // Validar se parece com IMEI (15 dígitos)
              const imeiPattern = /\b\d{15}\b/;

              if (imeiPattern.test(detectedText)) {
                console.log(`IMEI detectado na zona ${zone}:`, detectedText);

                // Atualizar zona ativa e status
                setActiveZone(zone);
                setScannerStatus("found");

                // Chamar callback do componente pai
                onDetected(detectedText);
                setIsScanning(false);

                // Parar o scanning
                if (scanIntervalRef.current) {
                  clearInterval(scanIntervalRef.current);
                  scanIntervalRef.current = null;
                }

                // Parar o stream de vídeo
                if (videoRef.current?.srcObject) {
                  const videoStream = videoRef.current.srcObject as MediaStream;
                  const tracks = videoStream.getTracks();
                  tracks.forEach((track) => track.stop());
                  videoRef.current.srcObject = null;
                }

                // Fechar o scanner
                onClose();
                return true; // Sucesso
              }
            }
            return false;
          })
          .catch((error: any) => {
            // Ignora erros comuns de "código não encontrado"
            if (error.name !== "NotFoundException") {
              console.error(`Erro na decodificação da zona ${zone}:`, error);
            }
          });
      } catch (error) {
        console.error(`Erro ao processar canvas na zona ${zone}:`, error);
      }
      return false;
    };

    // Testar cada zona sequencialmente
    const foundInAnyZone = zonesToTest.some((zone) => testZone(zone));

    // Se chegou aqui, nenhuma zona detectou código
    setActiveZone(null);
  }, [isMobile, onDetected, onClose]);

  // Função para iniciar o scanner (idêntica ao TestBarCode)
  const startScanning = useCallback(async () => {
    // console.log("Função startScanning chamada");
    setIsScanning(true);
    setCameraReady(false);
    try {
      setScannerStatus("searching");
      // Verificar se mediaDevices é suportado
      if (!navigator.mediaDevices) {
        throw new Error(
          "Acesso à câmera não suportado neste navegador ou ambiente (HTTPS necessário)"
        );
      }

      //   console.log("MediaDevices suportado");

      // Verificar permissões
      const permissionStatus = await navigator.permissions.query({
        name: "camera" as PermissionName,
      });
      //   console.log("Status da permissão:", permissionStatus.state);

      if (permissionStatus.state === "denied") {
        throw new Error(
          "Permissão de câmera negada. Verifique as configurações do seu navegador."
        );
      }

      if (!videoRef.current) {
        throw new Error("Elemento de vídeo não encontrado");
      }

      //   console.log("Solicitando acesso à câmera...");

      // Configurações otimizadas para capturar telas de celular
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment", // Câmera traseira
          width: isMobile
            ? { ideal: 640, max: 1280 }
            : { ideal: 1920, min: 1280 }, // Resolução menor no mobile
          height: isMobile
            ? { ideal: 480, max: 720 }
            : { ideal: 1080, min: 720 },
          frameRate: { ideal: 30, min: 15 }, // Frame rate alto
          // Remover configurações que podem forçar tela cheia no mobile
          ...(isMobile
            ? {}
            : {
                focusMode: "continuous",
                exposureMode: "continuous",
                whiteBalanceMode: "continuous",
              }),
        } as MediaTrackConstraints,
      });

      //   console.log("Stream obtido:", stream);

      videoRef.current.srcObject = stream;
      videoRef.current.play();

      //   console.log("Vídeo iniciado");
      setCameraReady(true);

      // Iniciar leitor após vídeo estar pronto
      videoRef.current.onloadedmetadata = async () => {
        // console.log("Metadados do vídeo carregados");
        try {
          // Configurar o leitor antes de usar
          setupCodeReader();

          if (!codeReader.current) {
            throw new Error("Leitor não configurado");
            return;
          }

          //   console.log("Iniciando escaneamento da área do viewFinder...");

          // Iniciar escaneamento por intervalo da área específica
          scanIntervalRef.current = setInterval(() => {
            scanMultipleAreas();
          }, 500); // Escanear a cada 500ms
        } catch (err) {
          console.error("Erro ao configurar escaneamento:", err);
        }
      };
    } catch (err) {
      console.error("Erro ao acessar câmera:", err);
      alert(
        `Erro ao acessar a câmera: ${
          err instanceof Error ? err.message : "Desconhecido"
        }`
      );
      onClose();
    }
  }, [isMobile, scanMultipleAreas, onClose]);

  // Iniciar scanner automaticamente quando modal abrir (apenas desktop)
  useEffect(() => {
    // console.log("useEffect executado. open:", open, "isMobile:", isMobile);
    if (open && !isMobile) {
      //   console.log("Desktop detectado, iniciando timer para startScanning...");
      // Pequeno delay para garantir que o modal renderizou
      const timer = setTimeout(() => {
        // console.log("Timer executado, chamando startScanning...");
        startScanning();
      }, 300);

      return () => {
        // console.log("Limpando timer...");
        clearTimeout(timer);
      };
    }

    if (open && isMobile) {
      //   console.log("Mobile detectado, aguardando ação manual do usuário...");
      setCameraReady(false);
    }

    // Não retorna nada quando open é false
    return undefined;
  }, [open, startScanning, isMobile]);

  // Função para fechar o scanner (idêntica ao TestBarCode)
  const closeScanner = useCallback(() => {
    // Parar qualquer stream ativo
    setIsScanning(false);
    setScannerStatus("idle");
    setActiveZone(null);
    setCameraReady(false);

    // Parar o intervalo de escaneamento
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }

    if (videoRef.current?.srcObject) {
      const tracks = (videoRef.current.srcObject as MediaStream).getTracks();
      tracks.forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }

    // Reiniciar o leitor
    codeReader.current = new BrowserMultiFormatReader();
    onClose();
  }, [onClose]);

  // Componente ScannerOverlay com ViewFinder Triplo
  const ScannerOverlay = () => {
    // Configurações dos 3 tamanhos (deve ser idêntica à função scanMultipleAreas)
    const viewFinderSizes = {
      small: {
        width: isMobile ? 180 : 200,
        height: isMobile ? 50 : 55,
        label: "Muito Próximo",
      },
      medium: {
        width: isMobile ? 220 : 240,
        height: isMobile ? 65 : 70,
        label: "Distância Ideal",
      },
      large: {
        width: isMobile ? 280 : 300,
        height: isMobile ? 80 : 85,
        label: "Distante",
      },
    };

    // Função para calcular zIndex sem ternário aninhado
    const getZIndex = (zone: string) => {
      if (zone === "small") return 3;
      if (zone === "medium") return 2;
      return 1;
    };

    return (
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        {/* Área escura ao redor */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          }}
        />

        {/* Retângulos dos 3 tamanhos */}
        {Object.entries(viewFinderSizes).map(([zone, size]) => {
          const isActive = activeZone === zone;
          const isFound = scannerStatus === "found" && isActive;

          return (
            <Box
              key={zone}
              sx={{
                position: "absolute",
                width: `${size.width}px`,
                height: `${size.height}px`,
                border: `${isActive ? 4 : 2}px ${
                  zone === "large" ? "dashed" : "solid"
                }`,
                borderColor: (() => {
                  if (isFound) return "#4caf50"; // Verde - encontrou
                  if (isActive) return "#ff9800"; // Laranja - zona ativa
                  if (zone === "small") return "#2196f3"; // Azul - pequeno
                  if (zone === "medium") return "#9c27b0"; // Roxo - médio
                  return "#607d8b"; // Cinza - grande
                })(),
                borderRadius: "8px",
                backgroundColor: "transparent",
                zIndex: getZIndex(zone),
                animation: isActive ? "pulse 1s infinite" : "none",

                "@keyframes pulse": {
                  "0%": { opacity: 1, transform: "scale(1)" },
                  "50%": { opacity: 0.7, transform: "scale(1.02)" },
                  "100%": { opacity: 1, transform: "scale(1)" },
                },
              }}
            >
              {/* Label da zona */}
              <Typography
                variant="caption"
                sx={{
                  position: "absolute",
                  top: "-25px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  color: "#fff",
                  backgroundColor: "rgba(0, 0, 0, 0.8)",
                  padding: "2px 6px",
                  borderRadius: "4px",
                  fontSize: "10px",
                  fontWeight: isActive ? "bold" : "normal",
                  display: isActive ? "block" : "none",
                }}
              >
                {size.label}
              </Typography>
            </Box>
          );
        })}

        {/* Texto de instrução geral */}
        <Typography
          variant="caption"
          sx={{
            position: "absolute",
            bottom: "-40px",
            left: "50%",
            transform: "translateX(-50%)",
            color: "#fff",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            padding: "6px 12px",
            borderRadius: "8px",
            whiteSpace: "nowrap",
            textAlign: "center",
          }}
        >
          {(() => {
            if (scannerStatus === "found") return "✓ IMEI Detectado!";
            if (activeZone)
              return `🔍 Escaneando ${viewFinderSizes[activeZone].label}...`;
            return "📱 Posicione o IMEI em qualquer retângulo";
          })()}
        </Typography>

        {/* Indicador de escaneamento ativo */}
        {scannerStatus === "searching" && activeZone && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "3px",
              height: `${viewFinderSizes[activeZone].height * 0.6}px`,
              backgroundColor: "#ff9800",
              animation: "scanLine 1.5s infinite",
              zIndex: 10,
              "@keyframes scanLine": {
                "0%": { opacity: 0.3 },
                "50%": { opacity: 1 },
                "100%": { opacity: 0.3 },
              },
            }}
          />
        )}
      </Box>
    );
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={closeScanner}
      maxWidth={isMobile ? false : "sm"}
      fullWidth
      fullScreen={isMobile}
      sx={{
        "& .MuiDialog-paper": {
          ...(isMobile && {
            margin: 0,
            width: "100%",
            height: "100%",
            maxHeight: "none",
          }),
        },
      }}
    >
      <DialogTitle
        sx={{
          display: isMobile ? "none" : "block", // Esconder título no mobile
        }}
      >
        {title}
        <IconButton
          aria-label="close"
          onClick={closeScanner}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Botão de fechar sobreposto no mobile */}
      {isMobile && (
        <IconButton
          aria-label="close"
          onClick={closeScanner}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            zIndex: 1000,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            color: "#fff",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.7)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      )}

      <DialogContent
        sx={{
          p: isMobile ? 0 : 2,
          height: isMobile ? "100vh" : "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: isMobile ? "100%" : "400px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: isMobile ? "100%" : "300px",
              borderRadius: isMobile ? 0 : "8px",
              overflow: "hidden",
            }}
          >
            <video
              ref={videoRef}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
              playsInline // Importante para evitar fullscreen no iOS
              muted // Importante para autoplay em alguns navegadores
            >
              <track kind="captions" />
            </video>

            {/* Canvas oculto para processamento da área do viewFinder */}
            <canvas
              ref={canvasRef}
              style={{
                display: "none", // Oculto - usado apenas para processamento
              }}
            />

            {cameraReady && <ScannerOverlay />}
          </Box>

          {/* Estados da câmera - Posicionamento diferente no mobile */}
          {isMobile && !isScanning && !cameraReady && (
            <Box
              sx={{
                position: "absolute",
                bottom: "20%",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 10,
                width: "90%",
                maxWidth: "400px",
              }}
            >
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>📱 Scanner Mobile</AlertTitle>
                Instruções principais: <br />
                <Box
                  sx={{
                    fontWeight: "bold",
                    color: "#d32f2f",
                    border: "1px solid #000",
                    padding: "8px",
                    borderRadius: "4px",
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                    margin: "8px 0",
                    display: "block", // Força comportamento de bloco
                  }}
                >
                  Posicione o retângulo sobre o IMEI tentando encaixar as linhas
                  pretas dentro da área delimitada
                </Box>
                Toque no botão abaixo para iniciar a câmera
              </Alert>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                onClick={startScanning}
                startIcon={<CameraAltIcon />}
              >
                Iniciar Câmera
              </Button>
            </Box>
          )}

          {!isMobile && !isScanning && !cameraReady && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <AlertTitle>Aguarde</AlertTitle>
              Inicializando câmera automaticamente...
            </Alert>
          )}

          {isScanning && !cameraReady && (
            <Box
              sx={{
                position: isMobile ? "absolute" : "static",
                bottom: isMobile ? "30%" : "auto",
                left: isMobile ? "50%" : "auto",
                transform: isMobile ? "translateX(-50%)" : "none",
                zIndex: 10,
                width: isMobile ? "90%" : "auto",
              }}
            >
              <Alert severity="info">
                <AlertTitle>Aguarde</AlertTitle>
                Solicitando acesso à câmera...
              </Alert>
            </Box>
          )}

          {/* Instruções quando câmera está ativa - Sobreposta no mobile */}
          {cameraReady && isScanning && (
            <Box
              sx={{
                position: isMobile ? "absolute" : "static",
                bottom: isMobile ? "10%" : "auto",
                left: isMobile ? "50%" : "auto",
                transform: isMobile ? "translateX(-50%)" : "none",
                zIndex: 10,
                width: isMobile ? "90%" : "auto",
                backgroundColor: isMobile
                  ? "rgba(0, 0, 0, 0.7)"
                  : "transparent",
                borderRadius: isMobile ? "8px" : 0,
                padding: isMobile ? 2 : 0,
              }}
            >
              <Typography
                variant="body2"
                color={isMobile ? "#fff" : "text.secondary"}
                textAlign="center"
                sx={{ mt: isMobile ? 0 : 2 }}
              >
                📱 Posicione a câmera sobre a tela do celular mostrando o IMEI
                <br />
                💡 Mantenha a tela bem iluminada e foque no código
                <br />
                {/* 🎯 Use o delimitador laranja para enquadrar o código */}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      {!isMobile && (
        <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={closeScanner} variant="outlined">
            Cancelar
          </Button>
        </Box>
      )}
    </Dialog>
  );
};

export default IMEIScanner;
